/**
 * O catálogo da Configurações — as seções, os itens e para onde cada um leva.
 *
 * É a fonte única do menu lateral, da busca do topo, das URLs por seção e —
 * desde a rodada CASCA — do **roteamento**. Um item que não está aqui não existe
 * na Configurações.
 *
 * ── Por que o destino é dado, e não deduzido
 *
 * Em 01/08 dois itens de menu sobreviveram às rotas que eles abriam, e a
 * sidebar ficou com dois links pra lugar nenhum — typecheck verde, build verde,
 * teste de grupos verde. Quem descobre é quem clica.
 *
 * ⚠️ **Este cabeçalho afirmava que "cada `para` é conferido por teste contra as
 * rotas declaradas no `App.tsx`", e citava um `configuracoes-catalogo.test.ts`.
 * Esse teste NUNCA EXISTIU.** Os três testes da pasta cobrem forma do catálogo,
 * caminho de entrada e o `?de=` da volta — nenhum confere destino contra rota.
 *
 * O preço foi cobrado: `/admin/notificacoes` (seção Sistema) apontava para uma
 * rota inexistente e caía no catch-all 404, e ninguém viu, **porque o comentário
 * dizia que havia guarda**. Documento que promete guarda inexistente é pior que
 * documento nenhum: desliga a desconfiança de quem lê.
 *
 * O guarda de verdade entra no K-2 desta rodada, escrito do zero.
 *
 * ── `?aba=` não é detalhe
 *
 * Quatro itens moram dentro da `ConfigCrmPage`, que tem 10 abas. Até 01/08 essas
 * abas não tinham endereço: apontar pra elas jogaria a pessoa na aba 1 de 10 —
 * trocar dois níveis de navegação por dois níveis de navegação, que é o que esta
 * tela existe pra matar. A migração pra `?aba=` foi feita antes, de propósito.
 */

/** Ícone: nome de um ícone do lucide-react. NUNCA emoji (regra do projeto). */
export type IconeSecao =
  | 'key'
  | 'tag'
  | 'trending-up'
  | 'dollar-sign'
  | 'settings'
  | 'plug'
  // ROTAS-ORFAS (06/08): a area Terceiros. `building` = empresa de fora.
  | 'building';

export interface ItemConfig {
  /**
   * Identificador na URL: `/admin/configuracoes/<secao>/<id>`.
   *
   * É **endereço**, não rótulo — mudá-lo quebra favorito e link em artigo de
   * ajuda. Por isso não é derivado do `nome`: renomear "Tags" para "Etiquetas"
   * na tela não pode mudar a URL de ninguém.
   */
  id: string;
  /** Rótulo curto, em português de gente. */
  nome: string;
  /** Uma linha dizendo o que a configuração FAZ — não o que ela é. */
  desc: string;
  /**
   * Destino ATUAL. Item já migrado para a casca aponta para
   * `/admin/configuracoes/<secao>/<id>` — o menu leva direto ao painel novo, não
   * ao redirect. Precisa existir no `App.tsx` (`destino-existe.test.ts` confere).
   */
  para: string;
  /**
   * Onde o item morava ANTES da casca (rodada CASCA, K-2).
   *
   * Serve só para gerar o redirect: há favorito, link em artigo de ajuda e link
   * em notificação apontando para o endereço velho, e já tivemos link morto na
   * sidebar por remover cedo demais. Ausente = item ainda não migrado.
   */
  rotaAntiga?: string;
  /**
   * O texto do "?" ao lado do título, quando o item está aberto (K-5).
   *
   * Diz o que a configuração faz **e o que ela NÃO faz** — a segunda metade é a
   * que evita o passeio pelas seções. Ausente = cai na `desc`, que já é uma
   * frase útil; o "?" nunca fica sem texto.
   */
  ajuda?: string;
  /**
   * Palavras que a busca casa além do nome e da descrição — como a pessoa
   * chamaria a coisa, não como o sistema chama. "alíquota" acha por "imposto".
   */
  busca: string;
  /**
   * Chave do catálogo de acesso (`acesso_tela`). Quando ausente, o item é
   * admin-only — que é o caso da maioria das configurações.
   */
  chave?: string;
  /**
   * Flag de módulo que precisa estar LIGADA para o item existir no menu.
   *
   * Não é permissão: `chave` responde "quem pode ver", isto responde "isto
   * existe nesta instalação". Módulo desligado esconde o item **inclusive para
   * admin** — na tela antiga a aba nem era renderizada, e migrar sem essa
   * condição faria o menu oferecer parâmetro de um módulo que a casa não usa.
   */
  flag?: string;
}

export interface SecaoConfig {
  id: string;
  nome: string;
  /** Uma linha explicando o que se resolve nesta seção. */
  desc: string;
  icone: IconeSecao;
  itens: ItemConfig[];
}

export const SECOES_CONFIG: SecaoConfig[] = [
  {
    id: 'pessoas',
    nome: 'Pessoas e acesso',
    desc: 'Quem entra no sistema e o que cada um consegue abrir.',
    icone: 'key',
    itens: [
      {
        id: 'permissoes',
        nome: 'Quem pode abrir o quê',
        desc: 'O resultado final por pessoa, com a origem de cada permissão.',
        para: '/admin/configuracoes/pessoas/permissoes',
        rotaAntiga: '/admin/permissoes',
        busca: 'permissao acesso liberar bloquear quem pode abrir excecao perfil departamento',
      },
      {
        // Aba "Acesso e carteira" da ConfigCrmPage — não estava no catálogo.
        // Destino decidido pelo Matheus: Pessoas e acesso. É sobre QUEM enxerga
        // quais leads, então é assunto de acesso, não de Comercial.
        id: 'acesso-carteira',
        nome: 'Acesso e carteira',
        desc: 'Quem enxerga quais leads no funil, e como a carteira é dividida.',
        para: '/admin/configuracoes/pessoas/acesso-carteira',
        rotaAntiga: '/admin/comercial/crm?aba=acesso',
        busca: 'acesso carteira visibilidade lead vendedor escopo quem ve',
      },
      {
        id: 'usuarios',
        nome: 'Usuários',
        desc: 'Quem entra no sistema.',
        para: '/admin/configuracoes/pessoas/usuarios',
        rotaAntiga: '/admin/usuarios',
        busca: 'usuario pessoa colaborador login conta funcionario',
      },
      {
        id: 'perfis',
        nome: 'Perfis',
        desc: 'Conjuntos de permissão reutilizáveis.',
        para: '/admin/configuracoes/pessoas/perfis',
        rotaAntiga: '/admin/perfis',
        busca: 'perfil papel funcao cargo',
      },
      {
        id: 'departamentos',
        nome: 'Departamentos',
        desc: 'Comercial, Financeiro, Manutenção e os demais.',
        para: '/admin/configuracoes/pessoas/departamentos',
        rotaAntiga: '/admin/departamentos',
        busca: 'departamento setor area equipe time',
      },
    ],
  },
  {
    id: 'catalogos',
    nome: 'Catálogos',
    desc: 'As listas que alimentam os campos de seleção do sistema.',
    icone: 'tag',
    itens: [
      {
        id: 'listas-crm',
        nome: 'Listas do CRM',
        desc: 'Motivos de perda, origem do lead, motivos de descarte.',
        para: '/admin/configuracoes/catalogos/listas-crm',
        rotaAntiga: '/admin/comercial/crm?aba=listas',
        busca: 'motivo perda perdido origem lead captacao fonte descarte lista',
      },
      {
        id: 'tipos-atividade',
        nome: 'Tipos de atividade',
        desc: 'Ligação, e-mail, visita, WhatsApp.',
        para: '/admin/configuracoes/catalogos/tipos-atividade',
        rotaAntiga: '/admin/tipos-atividade',
        busca: 'tipo atividade ligacao email visita whatsapp timeline',
      },
      {
        id: 'catalogos-contrato',
        nome: 'Catálogos de contrato',
        desc: 'Tipos, status, formas de pagamento e vigências.',
        para: '/admin/configuracoes/catalogos/catalogos-contrato',
        rotaAntiga: '/admin/clientes/catalogos',
        busca: 'catalogo contrato tipo status pagamento vigencia',
      },
      {
        id: 'tags',
        nome: 'Tags',
        desc: 'Etiquetas para agrupar clientes e cards.',
        para: '/admin/configuracoes/catalogos/tags',
        rotaAntiga: '/admin/catalogos/tags',
        busca: 'tag etiqueta rotulo marcador agrupar segmento',
      },
      {
        /**
         * Aba "Compras" dos Catálogos — migrada em 09/08/2026.
         *
         * Na tela antiga ela renderizava `<ComprasConfigPage embedded />`, e o
         * `embedded` existia só para não repetir o título da página hospedeira.
         * Aqui entra a forma NORMAL: dentro da casca o `PageHeader` se cala
         * sozinho (`useCasca`), enquanto o `embedded` desenharia um `<h2>` — um
         * segundo título, que é o que a casca veio remover.
         *
         * A flag acompanha: sem `COMPRAS_ENABLED` a aba não era renderizada lá,
         * e não pode passar a aparecer aqui.
         */
        id: 'compras',
        nome: 'Catálogos de Compras',
        desc: 'Tipos de item, setores, formas de pagamento, fornecedores e a alíquota de NF por empresa.',
        para: '/admin/configuracoes/catalogos/compras',
        flag: 'COMPRAS_ENABLED',
        busca: 'compras cotacao fornecedor tipo item setor pagamento aliquota nota fiscal nf imposto empresa',
        ajuda:
          'As listas que o módulo de Compras usa para montar uma cotação, mais o cadastro de fornecedores. A alíquota de NF por empresa também é editada aqui — é o mesmo campo que o Financeiro aponta em "Impostos".',
      },
      {
        /**
         * Aba "Comercial" dos Catalogos — migrada em 09/08/2026, a ULTIMA.
         *
         * As tres listas eram uma sidebar trocando a prop `list` da mesma
         * `CatalogTable`. Cada uma ganhou endereco proprio; a tabela continua
         * sendo uma implementacao so (ver `catalogos-wrappers.tsx`).
         *
         * Sem flag: o Comercial nao depende de modulo ligado.
         */
        id: 'produtos',
        nome: 'Produtos',
        desc: 'A lista de produtos que aparece ao montar uma proposta.',
        para: '/admin/configuracoes/catalogos/produtos',
        busca: 'produto item equipamento camera sensor lista comercial',
      },
      {
        id: 'servicos',
        nome: 'Serviços',
        desc: 'Os serviços vendidos — e o campo que as metas e o filtro do funil usam.',
        para: '/admin/configuracoes/catalogos/servicos',
        busca: 'servico cftv cerca portaria monitoramento manutencao meta filtro',
        ajuda:
          'Esta lista alimenta o campo Serviço da proposta, o filtro do funil e as metas por serviço. Mexer aqui muda os três ao mesmo tempo.',
      },
      {
        id: 'origens',
        nome: 'Origens do lead',
        desc: 'De onde o lead chegou — indicação, site, telefone.',
        para: '/admin/configuracoes/catalogos/origens',
        busca: 'origem lead indicacao site telefone whatsapp captacao fonte',
      },
      // PROJETOS-LIMPO 2 (14/09/2026): "Atividades padrão de projeto" MORREU — decisão do
      // Matheus (seis tarefas intocadas desde 01/07; o Cronograma faz isso melhor).
      {
        id: 'projeto-documentos',
        // PROJETOS-LIMPO 2 (14/09): é regra do GANHO (o Comercial anexa; sem eles a venda não
        // vira projeto) — na tela de Catálogos mora em Comercial.
        nome: 'Documentos exigidos no Ganho',
        desc: 'O que o Comercial precisa anexar para a venda virar projeto.',
        para: '/admin/configuracoes/catalogos/projeto-documentos',
        flag: 'PROJETOS_V2_ENABLED',
        busca: 'projeto documento anexo exigido obrigatorio arquivo ganho comercial venda',
      },
      {
        id: 'projeto-tipos',
        nome: 'Tipos de projeto',
        desc: 'As categorias que classificam um projeto.',
        para: '/admin/configuracoes/catalogos/projeto-tipos',
        // SEM rotaAntiga de proposito: /admin/projeto-tipos AINDA e rota de
        // verdade no App.tsx e serve a pagina. Declarar o redirect geraria
        // configuracao morta — a guarda destino-existe pegou isso na hora. O
        // redirect entra quando a rota antiga sair, junto com o portal.
        flag: 'PROJETOS_V2_ENABLED',
        busca: 'projeto tipo categoria classificacao',
      },
      {
        /**
         * Aba "Avançados" dos Catálogos — migrada em 09/08/2026.
         *
         * Sem `flag`, e sim com guarda de PERFIL: esta aba nunca dependeu de
         * módulo ligado, e sim de quem está olhando (super-admin ou
         * administrativo). A guarda viaja com a tela no wrapper
         * `CatalogosAvancados` — `CatalogsR2Page` não se protege sozinha.
         *
         * O nome perdeu o "R2" quando a tela virou uma área (Faxina2/FB1); aqui
         * ele continua fora, porque "R2" é nome técnico que vazava.
         */
        id: 'avancados',
        nome: 'Catálogos avançados',
        desc: 'Categorias de serviço, papéis de vínculo e modelos de contrato.',
        para: '/admin/configuracoes/catalogos/avancados',
        busca: 'avancado categoria servico papel vinculo template modelo contrato r2',
        ajuda:
          'Catálogos estruturais, mexidos raramente: as categorias que agrupam serviços, os papéis de vínculo e os modelos de contrato. Não é aqui que se cria um contrato — só o modelo dele.',
      },
      /**
       * ROTAS-ORFAS → AUTOMACOES-SAI (07/08/2026): o item de Automações que
       * nasceu aqui ontem SAIU hoje, e a decisão mudou por um fato meu errado.
       *
       * Eu havia reportado "1 regra cadastrada, desligada". A regra é da tabela
       * `automacoes` — do CRM, outra tela. A tela `/admin/automacoes` lê
       * `automations`, que tem **0 linhas**: está vazia. Com zero regras, o
       * Matheus decidiu tirá-la agora e trazê-la de volta quando a Manutenção
       * subir e a função fizer sentido.
       *
       * ⚠️ O MOTOR FICA INTACTO: `components/automations/AutomationsPanel` é
       * usado hoje por Compras e Manutenção, e o endpoint continua servindo.
       * Só a tela de administração saiu.
       */
      {
        /**
         * ⚠️ SEM DECISÃO EXPLÍCITA do Matheus. Questionários não estava entre as
         * 7 telas que ele decidiu (1 template, 0 respostas, espera a Manutenção
         * subir) — é o irmão idêntico de Automações logo acima, e recebeu o
         * mesmo destino para NÃO ficar inalcançável quando a barra antiga caiu.
         * Registrado no relatório da rodada para ele confirmar ou remanejar.
         */
        id: 'questionarios',
        nome: 'Questionários',
        desc: 'Os checklists que o técnico responde na ordem de serviço.',
        para: '/admin/questionarios',
        busca: 'questionario checklist pergunta formulario os tecnico vistoria',
        // 26/10/2026: a frase antiga ("espera o módulo Manutenção entrar no ar") envelheceu com a OS do sistema no ar.
        ajuda:
          'Crie aqui o questionário e as perguntas. Depois escolha em quais serviços ele vale, em Configurações › Ordens de serviço.',
      },
      {
        id: 'campos-personalizados',
        nome: 'Campos personalizados',
        desc: 'Campos extras em cliente, lead e ordem de serviço.',
        para: '/admin/configuracoes/catalogos/campos-personalizados',
        rotaAntiga: '/admin/campos-personalizados',
        busca: 'campo personalizado extra customizado formulario',
      },
    ],
  },
  {
    id: 'comercial',
    nome: 'Comercial',
    desc: 'Como o funil, as propostas e os lembretes se comportam.',
    icone: 'trending-up',
    itens: [
      {
        id: 'fases-funil',
        nome: 'Fases do funil',
        desc: 'As colunas e o que cada uma exige para avançar.',
        para: '/admin/configuracoes/comercial/fases-funil',
        rotaAntiga: '/admin/comercial/crm?aba=fases',
        busca: 'fase funil coluna etapa pipeline gatilho kanban',
      },
      {
        id: 'lembretes',
        nome: 'Tempos de lembrete',
        desc: 'Quantos minutos antes avisar da atividade agendada.',
        para: '/admin/configuracoes/comercial/lembretes',
        rotaAntiga: '/admin/comercial/crm?aba=lembretes',
        busca: 'lembrete aviso alerta minuto atividade agendamento notificar',
      },
      {
        /**
         * C6-B · 1f (09/08/2026) — a espera minima para unificar propostas.
         *
         * Fica em Comercial, e nao em Catalogos: nao e uma LISTA que alimenta
         * campo de selecao, e uma regra de como o funil se comporta — mesmo
         * assunto de fases, lembretes e reciclagem.
         */
        id: 'unificar-propostas',
        nome: 'Unificar propostas',
        desc: 'Quantos dias uma proposta precisa ter para poder ser unificada com outra.',
        para: '/admin/configuracoes/comercial/unificar-propostas',
        busca: 'unificar unificacao juntar agrupar proposta cooldown espera dias carencia',
        ajuda:
          'A espera minima entre a criacao de uma proposta e a possibilidade de unifica-la. Nao muda meta nenhuma: propostas unificadas continuam contando no mes em que nasceram.',
      },
      {
        id: 'orcamento-precos',
        nome: 'Orçamento · preços',
        desc: 'Markups, mão de obra, régua e faixas.',
        para: '/admin/configuracoes/comercial/orcamento-precos',
        rotaAntiga: '/admin/comercial/crm?aba=orcamento-precos',
        busca: 'orcamento preco markup margem mao de obra regua faixa',
      },
      {
        id: 'orcamento-kits',
        nome: 'Orçamento · kits e taxas',
        desc: 'Kits prontos e as taxas dos simuladores.',
        para: '/admin/configuracoes/comercial/orcamento-kits',
        rotaAntiga: '/admin/comercial/crm?aba=orcamento-kits',
        busca: 'kit taxa simulador orcamento combo',
      },
      {
        // Aba "Modelos de orçamento" da ConfigCrmPage — não estava no catálogo.
        // Destino decidido pelo Matheus: seção Comercial.
        id: 'modelos-orcamento',
        nome: 'Modelos de orçamento',
        desc: 'Modelos nomeados que o vendedor aplica ao montar uma proposta.',
        para: '/admin/configuracoes/comercial/modelos-orcamento',
        rotaAntiga: '/admin/comercial/crm?aba=modelos',
        busca: 'modelo orcamento template proposta padrao nomeado',
      },
      {
        // Aba "Reciclagem de leads" — idem.
        id: 'reciclagem-leads',
        nome: 'Reciclagem de leads',
        desc: 'Quando um lead perdido volta para a fila, e por qual regra.',
        para: '/admin/configuracoes/comercial/reciclagem-leads',
        rotaAntiga: '/admin/comercial/crm?aba=reciclagem',
        busca: 'reciclagem lead perdido voltar fila retomar reaproveitar',
      },
      {
        id: 'metas',
        nome: 'Metas',
        desc: 'Metas de faturamento e propostas por vendedor.',
        para: '/admin/comercial/metas',
        busca: 'meta objetivo faturamento vendedor comissao',
        chave: 'comercial.metas.ver',
      },
    ],
  },
  {
    id: 'financeiro',
    nome: 'Financeiro',
    desc: 'Impostos, contas e o que o sistema considera despesa.',
    icone: 'dollar-sign',
    itens: [
      {
        id: 'contas',
        nome: 'Contas bancárias',
        desc: 'Quais contas entram nos números do Financeiro.',
        para: '/admin/configuracoes/financeiro/contas',
        rotaAntiga: '/admin/configuracoes/geral#financeiro',
        busca: 'conta banco bancaria saldo entra numeros',
      },
      {
        id: 'metas-mes',
        nome: 'Metas do mês',
        desc: 'Os alvos de faturamento e despesa que os painéis comparam.',
        para: '/admin/configuracoes/financeiro/metas-mes',
        busca: 'meta mes alvo faturamento despesa objetivo',
      },
      {
        id: 'financiamento',
        nome: 'Financiamento',
        desc: 'O que o sistema trata como financiamento — e por isso não conta como lucro.',
        para: '/admin/configuracoes/financeiro/financiamento',
        // D-3 (#57): "imposto"/"aliquota" SAÍRAM daqui. Eram herança do item
        // antigo "Contas, metas e IMPOSTOS" e mandavam a busca pra uma tela
        // sem alíquota nenhuma. Decisão do Matheus: o Financeiro APONTA — o
        // item "Impostos" abaixo leva ao editor real, em Compras.
        busca: 'financiamento emprestimo capital lucro margem categoria',
      },
      {
        id: 'impostos',
        nome: 'Impostos (alíquota de NF)',
        desc: 'A alíquota é editada em Compras — este atalho leva direto lá.',
        // D-3 (#57): o editor real é o `empresas.aliquota_nf_pct` na aba
        // Compras dos catálogos (ficha D-14, operacional — o Orçamento lê o
        // mesmo campo). Nada muda de lugar: o Financeiro só aponta.
        // MIGROU em 09/08/2026, junto com a aposentadoria do portal "Catálogos
        // por área": era a condição que eu tinha declarado. Enquanto a aba de
        // Compras só existia na tela antiga, apontar para a casa nova criaria
        // dois itens com o mesmo destino — adivinhação, e há guarda contra
        // isso. Agora o portal saiu e o destino é único.
        para: '/admin/configuracoes/catalogos/compras#aliquota-nf',
        ajuda:
          'A alíquota de nota fiscal por empresa é editada em Configurações › Catálogos › Catálogos de Compras. Este item existe pra você não procurar no Financeiro o que mora lá — ele leva direto ao bloco das empresas.',
        busca: 'imposto impostos aliquota nota fiscal nf tributo taxa',
      },
      {
        id: 'relatorios',
        nome: 'Envio de relatórios',
        desc: 'Quem recebe o resumo financeiro, e com que frequência.',
        para: '/admin/configuracoes/financeiro/relatorios',
        busca: 'relatorio envio email resumo frequencia destinatario',
      },
    ],
  },
  {
    id: 'sistema',
    nome: 'Sistema',
    desc: 'Expediente, avisos e o que está ligado.',
    icone: 'settings',
    itens: [
      {
        id: 'horario-funcionamento',
        nome: 'Horário de funcionamento e feriados',
        desc: 'Expediente da empresa — é a base do cálculo de prazo.',
        para: '/admin/configuracoes/horario-funcionamento',
        busca: 'horario expediente funcionamento jornada prazo dia util feriado folga calendario',
      },
      {
        id: 'preenchimento-obrigatorio',
        nome: 'Preenchimento obrigatório',
        desc: 'O que a equipe é obrigada a informar no cliente e no card.',
        para: '/admin/configuracoes/geral#sistema',
        busca: 'obrigatorio campo exigir preenchimento required validacao',
      },
      // "Notificações" saiu daqui em 2026-08-02 (decisão do Matheus, K-2).
      //
      // Apontava para `/admin/notificacoes`, que NUNCA existiu: quem clicava
      // caía no catch-all 404. O item foi catalogado antes da tela — e o guarda
      // que teria pego isso (`destino-existe.test.ts`) só nasceu agora.
      //
      // Não foi reapontado para `/perfil/notificacoes` de propósito: aquela é a
      // tela pessoal de cada usuário, não a configuração de notificações do
      // sistema. Mandar para lá esconderia a falta atrás de um destino plausível.
      //
      // Fica como pendência no `BUGS.md` até existir a tela.
      {
        // FUNDACOES A1 (17/08): o que quebrou pra alguém, registrado sozinho.
        // Admin-only (a rota GET é requireAdmin; a casca segue o catálogo).
        id: 'erros-de-tela',
        nome: 'Erros de tela',
        desc: 'O que quebrou pra alguém, registrado sozinho — sem depender de aviso.',
        para: '/admin/configuracoes/sistema/erros-de-tela',
        busca: 'erro tela quebrou crash bug registro monitoramento javascript stack',
      },
      {
        // Aba "Alertas" da ConfigCrmPage — não estava no catálogo. Destino
        // decidido pelo Matheus: Sistema. São cortes numéricos que governam
        // avisos, não configuração do funil.
        id: 'alertas',
        nome: 'Alertas',
        desc: 'A partir de quantos dias ou de qual valor o sistema chama atenção.',
        para: '/admin/configuracoes/sistema/alertas',
        rotaAntiga: '/admin/comercial/crm?aba=alertas',
        busca: 'alerta aviso corte limite atencao quadro parado',
      },
      {
        /**
         * HOME-POR-PERFIL (08/08/2026) — a tela ganhou casa porque ganhou
         * função.
         *
         * Ela existia desde a R-IMPORT 3 e ficou anos sem porta de entrada, o
         * que estava CERTO enquanto ela não servia para nada: o builder gravava
         * um layout que a Home nunca lia (o §6.12 do MAPA). Classificá-la como
         * "sem-link intencional" era verdade sobre o mundo daquele dia.
         *
         * Deixou de ser verdade em 07/08: a Home passou a ler o layout, e a
         * tela virou o lugar onde se monta o PADRÃO de cada perfil — o pedido
         * original do Matheus. Função no ar que ninguém alcança é pior que
         * função que não existe: promete e não entrega.
         *
         * Item sem `chave` = admin-only, que espelha o `requireAdmin` do
         * `PUT /dashboard/layout/perfil/:slug`.
         */
        id: 'home-por-perfil',
        nome: 'Home por perfil',
        desc: 'Monte o que cada perfil vê ao entrar no sistema — e a sua própria home.',
        para: '/admin/dashboard/builder',
        busca: 'home inicial dashboard widget painel personalizar montar perfil layout tela de entrada',
        ajuda:
          'Arraste os blocos para montar a home. Em "Salvar para" você escolhe se aquilo vale só para você ou vira o padrão de um perfil inteiro — quem já ajustou a própria home continua com o ajuste dele.',
      },
      {
        /**
         * ROTAS-ORFAS (06/08) — decisão 3, a mais sensível das sete.
         *
         * São as sequências de etapas por onde os cards caminham: 8 cadastradas,
         * e o Comercial inteiro roda em cima de uma delas. Item SEM `chave` =
         * admin-only (é o padrão do catálogo), e a mesma restrição vale no
         * SERVIDOR — o Matheus pediu explicitamente para não repetir o inverso
         * do que apareceu no Compras (front liberando o que a rota nega).
         */
        id: 'workflows',
        nome: 'Sequências de etapas (workflows)',
        desc: 'As colunas por onde um card caminha, do início ao fim.',
        para: '/admin/workflows',
        busca: 'workflow fluxo sequencia etapa fase coluna kanban funil processo',
        ajuda:
          'Mexer aqui altera o funil de todo o time — o Comercial roda em cima de uma destas sequências. Só administrador abre e edita.',
      },
      {
        id: 'recursos-ativos',
        nome: 'Recursos ativos',
        desc: 'O que está ligado no sistema.',
        para: '/admin/configuracoes/sistema/recursos-ativos',
        rotaAntiga: '/admin/feature-flags',
        busca: 'recurso feature flag ligado desligado modulo funcionalidade',
      },
      {
        // Frente 4 (02/09): a herança global → cliente → equipamento já
        // existia; faltava a tela para o nível global.
        id: 'monitoramento-padroes',
        nome: 'Monitoramento — padrões',
        desc: 'Intervalo do teste, falhas até declarar queda e retenção do histórico.',
        para: '/admin/configuracoes/sistema/monitoramento-padroes',
        busca: 'monitoramento coletor equipamento ping intervalo falha queda padrao heranca cftv camera',
      },
      {
        // OS DO SISTEMA (fase 3, 25/10/2026; decisão do Matheus de 24/10: a configuração da OS mora em Configurações).
        id: 'ordens-de-servico',
        nome: 'Ordens de serviço',
        desc: 'Os serviços, o que cada um exige, a distância do check-in e quem usa o aplicativo.',
        para: '/admin/configuracoes/sistema/ordens-de-servico',
        chave: 'manutencao.os.configurar',
        busca: 'ordem de servico os natureza servico regra foto assinatura cpf questionario checkin check-in distancia checkout lembrete piloto tecnico',
      },
      {
        // PESQUISA DE SATISFAÇÃO (mig 796, 28/10/2026; D30). Sem `chave`: só o admin (D56).
        id: 'pesquisa-de-satisfacao',
        nome: 'Pesquisa de satisfação',
        desc: 'A pesquisa depois do atendimento: quando sai, quanto tempo vale, as perguntas e os canais.',
        para: '/admin/configuracoes/sistema/pesquisa-de-satisfacao',
        ajuda: 'Nasce desligada. Uma pesquisa por cliente por dia; a primeira resposta fecha.',
        busca: 'pesquisa satisfacao nota avaliacao atendimento cliente portal whatsapp link',
      },
      {
        // ORÇAMENTO DA MANUTENÇÃO (mig 782, 27/10/2026). Sem `chave`: só o admin (D56).
        id: 'orcamento-da-manutencao',
        nome: 'Orçamento da Manutenção',
        desc: 'O markup, a mão de obra, os descontos, as formas de pagamento e os prazos do orçamento da Manutenção.',
        para: '/admin/configuracoes/sistema/orcamento-da-manutencao',
        ajuda:
          'O que a Manutenção usa ao montar um orçamento. O markup vem do catálogo do Comercial, mas o padrão daqui é só da Manutenção: mudar aqui não muda o Comercial.',
        busca: 'orcamento manutencao markup margem mao de obra diaria hora desconto pagamento parcela prazo entrega compras responder email',
      },
      {
        id: 'seguranca-login',
        nome: 'Segurança do login',
        desc: 'Limite de tentativas e destrave de conta.',
        para: '/admin/configuracoes/sistema/seguranca-login',
        rotaAntiga: '/admin/configuracoes/geral#seguranca',
        busca: 'seguranca login senha tentativa bloqueio destravar rate limit',
      },
    ],
  },
  {
    id: 'integracoes',
    nome: 'Integrações',
    desc: 'O que o sistema troca com outros sistemas.',
    icone: 'plug',
    itens: [
      {
        id: 'visao-geral',
        nome: 'Visão geral',
        desc: 'O estado de cada integração.',
        para: '/admin/configuracoes/integracoes/visao-geral',
        rotaAntiga: '/admin/integracoes',
        busca: 'integracao conexao api externo',
      },
      {
        id: 'omie',
        nome: 'Omie',
        desc: 'Freio de emergência, frequência e horários da conferência.',
        para: '/admin/configuracoes/integracoes/omie',
        busca: 'omie erp freio escrita cadencia frequencia conferencia empresa credencial',
      },
      {
        id: 'auvo',
        nome: 'Auvo',
        desc: 'Leitura das ordens de serviço em campo.',
        para: '/admin/configuracoes/integracoes/auvo',
        busca: 'auvo ordem servico campo tecnico chamado questionario leitura',
      },
      {
        id: 'whatsapp',
        nome: 'WhatsApp',
        desc: 'Avisos automáticos, comunicados e o freio.',
        para: '/admin/configuracoes/integracoes/whatsapp',
        busca: 'whatsapp zap mensagem aviso comunicado grupo freio evolution',
      },
      {
        // MODELOS DE E-MAIL (mig 786, 28/10/2026; D36). Sem `chave`: só o admin.
        id: 'email',
        nome: 'E-mail',
        desc: 'Os e-mails que saem sozinhos ao cliente, cada um com a sua chave.',
        para: '/admin/configuracoes/integracoes/email',
        ajuda: 'Os novos nascem desligados; desligado, nada sai. Os do orçamento saem sempre: só o texto se muda.',
        busca: 'email e-mail modelo atendimento finalizado resumo visita cliente sindico ligar desligar',
      },
      {
        id: 'google',
        nome: 'Google Agenda',
        desc: 'Leitura da agenda para o Comercial.',
        para: '/admin/configuracoes/integracoes/google',
        busca: 'google agenda calendar calendario compromisso',
      },
      {
        id: 'vinculos',
        nome: 'Vínculos de cliente',
        desc: 'Cadastros de fora esperando par no sistema.',
        para: '/admin/configuracoes/integracoes/vinculos',
        busca: 'vinculo cliente parear casar omie auvo cnpj documento fila',
      },
      {
        id: 'produtos-sumidos',
        nome: 'Produtos — sumiram do Omie',
        desc: 'Escondidos da busca do orçamento, e por quê.',
        para: '/admin/configuracoes/integracoes/produtos-sumidos',
        busca: 'produto sumido fantasma escondido varredura preco errado orcamento',
      },
      {
        id: 'omie-sincronizacao',
        nome: 'Omie — sincronização',
        desc: 'Estado da leitura de clientes, produtos e financeiro.',
        para: '/admin/configuracoes/integracoes/omie-sincronizacao',
        rotaAntiga: '/admin/omie/sync-status',
        busca: 'omie sync sincronizacao erp importar cliente produto',
      },
      {
        id: 'omie-auditoria',
        nome: 'Omie — trilha de auditoria',
        desc: 'O que foi lido e escrito, e quando.',
        para: '/admin/configuracoes/integracoes/omie-auditoria',
        rotaAntiga: '/admin/omie/audit-log',
        busca: 'omie auditoria log trilha historico',
      },
      {
        id: 'produtos-catalogo',
        nome: 'Produtos — catálogo único',
        desc: 'Os produtos canônicos que o autocomplete usa.',
        para: '/admin/configuracoes/integracoes/produtos-catalogo',
        rotaAntiga: '/admin/produtos/universal',
        busca: 'produto catalogo universal canonico unico item',
      },
      {
        id: 'produtos-conciliacao',
        nome: 'Produtos — fila de conciliação',
        desc: 'Produtos do Omie esperando virar item do catálogo.',
        para: '/admin/configuracoes/integracoes/produtos-conciliacao',
        rotaAntiga: '/admin/produtos/conciliar',
        busca: 'produto conciliar conciliacao fila revisar duplicado',
      },
    ],
  },
];

/**
 * Os ids das seções. Usado pelo `AdminLayout` pra saber que
 * `/admin/configuracoes/<id>` é a tela NOVA — e não deve receber a barra de abas
 * antiga por cima (G-2: duas navegações para o mesmo assunto).
 */
export const SECAO_IDS: ReadonlyArray<string> = SECOES_CONFIG.map((s) => s.id);

/** Todos os itens, achatados — a busca do topo trabalha sobre esta lista. */
export const ITENS_CONFIG = SECOES_CONFIG.flatMap((s) =>
  s.itens.map((i) => ({ ...i, secaoId: s.id, secaoNome: s.nome })),
);

export type ItemConfigComSecao = (typeof ITENS_CONFIG)[number];

/**
 * Normaliza para busca: minúsculas e **sem acento**, porque ninguém digita
 * "manutenção" com til na pressa — e "orcamento" tem de achar "Orçamento".
 */
export function normalizarBusca(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    // `\p{Diacritic}` em vez de um intervalo de combinantes escrito à mão: o
    // caractere combinante é INVISÍVEL no editor (parece um colchete vazio) e
    // não sobrevive a reencode. A classe Unicode diz a mesma coisa em ASCII.
    .replace(/\p{Diacritic}/gu, '');
}

/**
 * Filtra os itens por termo. Casa nome, descrição e as palavras de busca.
 *
 * Termo com menos de 2 caracteres devolve vazio de propósito: 1 letra casaria
 * quase tudo e a lista viraria ruído.
 */
export function buscarItens(termo: string, itens = ITENS_CONFIG): ItemConfigComSecao[] {
  const t = normalizarBusca(termo.trim());
  if (t.length < 2) return [];
  return itens.filter((i) =>
    normalizarBusca(`${i.nome} ${i.desc} ${i.busca} ${i.secaoNome}`).includes(t),
  );
}

/**
 * O que esta pessoa pode ver. Item sem `chave` é admin-only — a maioria das
 * configurações é. Item com `chave` aparece para quem tem aquela permissão, e é
 * assim que o vendedor enxerga "Metas" sem enxergar o resto.
 */
export function filtrarPorPermissao<T extends ItemConfig>(
  itens: T[],
  opts: { isAdmin: boolean; chaves: Set<string> },
): T[] {
  if (opts.isAdmin) return itens;
  return itens.filter((i) => (i.chave ? opts.chaves.has(i.chave) : false));
}

/**
 * Tira os itens de módulos desligados. Roda DEPOIS do filtro de permissão e vale
 * para todo mundo, admin incluído — ver o comentário de `ItemConfig.flag`.
 *
 * Flag ausente do mapa conta como desligada. É fail-safe de propósito: enquanto
 * as flags carregam o mapa vem vazio, e mostrar primeiro para esconder depois
 * faria o menu piscar itens de módulos que a casa nem tem.
 */
export function filtrarPorFlag<T extends ItemConfig>(
  itens: T[],
  flags: Record<string, boolean>,
): T[] {
  return itens.filter((i) => !i.flag || flags[i.flag] === true);
}
